<template>
  <div class="books__item" :class="[`books__item_${doc.doc_type}`, doc.id]">
    <template v-if="view.hasUserData(doc)">
      <SaveButton class="books__save-button" :doc="doc" :docsView="view" />
      <DocMoreMenu
        class="books__more-menu"
        :class="{
          'books__more-menu--above-progress': hasProgress && doc.user?.progress,
        }"
        :doc="doc"
        :docsView="view"
      />
    </template>
    <router-link :to="view.startLinkParams(doc)" class="books__link">
      <DocTypeBadge :docType="doc.doc_type" />
      <BookCardThumbnail
        v-if="doc.doc_type === 'book'"
        class="books__img-box"
        coverClass="books__img"
        fallbackCoverClass="books__img"
        :doc="doc"
        :cacheBust="true"
      />
      <div
        v-else
        class="books__fit_img-box"
        :class="{
          'books__fit_img-box--circle': doc.doc_type === 'podcast_episode',
        }"
      >
        <LazyImage
          :src="doc.cover_image"
          crossorigin="anonymous"
          class="books__fit_img"
          alt=""
        />
      </div>

      <DocProgressBar
        v-if="hasProgress"
        class="books__progress"
        :progress="doc.user?.progress"
      />
      <div class="books__text-box">
        <div class="books__text">
          <div class="books__upper-box">
            <h3 class="books__title card__title">{{ doc.title }}</h3>
            <h4 class="books__author card__author">{{ doc.author }}</h4>
          </div>
          <div class="books__lower-box">
            <DocBadges :doc="doc" :free="showFreeBooks && doc.is_free" />
            <DocProgressBar
              v-if="hasProgress && doc.user?.progress"
              class="books__progress--mobile"
              :progress="doc.user.progress"
            />
          </div>
        </div>
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import LazyImage from '@/app/components/podcast/card/LazyImage.vue'
import DocBadges from '@/app/components/doc/DocBadges.vue'
import DocMoreMenu from '@/app/components/doc/DocMoreMenu.vue'
import DocProgressBar from '@/app/components/doc/DocProgressBar.vue'
import DocTypeBadge from '@/app/components/doc/DocTypeBadge.vue'
import SaveButton from '@/app/components/doc/SaveButton.vue'

import type { DocListItem, CustomList } from '@/models/interfaces'
import type { DocsView } from '@/models/doc'

const props = withDefaults(
  defineProps<{
    doc: DocListItem
    view: DocsView
    customList?: CustomList
    /**
     * Show the "Free" badge on free books.
     */
    showFreeBooks?: boolean
  }>(),
  {},
)

const hasProgress = computed(() => {
  return props.doc.doc_type !== 'article'
})
</script>

<style lang="scss" scoped>
.books {
  &__title,
  &__author {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  &__save-button {
    position: absolute;
    z-index: 2;
    top: 12px;
    right: 12px;
  }
  &__more-menu {
    position: absolute;
    z-index: 2;
    right: 20px;
    bottom: 18px;

    @media (max-width: $w-tablet-s) {
      // Line up with the Save button.
      right: 12px;
      bottom: 17px;
    }

    // On mobile, show the menu button above the progress bar.
    &--above-progress {
      @media (max-width: $w-tablet-s) {
        bottom: 30px;
      }
    }
  }
  &__fit_img-box {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 173px;
    border-radius: 12px 12px 0 0;
    overflow: hidden;
    background-color: $c-black-5;
    @media only screen and (max-width: $w-tablet-s) {
      height: 163px;
    }
  }
  &__fit_img {
    /* The heights are chosen to match BookCardThumbnail at the reduced
       book cover size used in multiblock. */
    display: block;
    transition: all 0.2s ease-in-out;
    height: 173px;
    object-fit: cover;
    @media only screen and (max-width: $w-tablet-s) {
      height: 163px;
    }
  }
  // Show a circular cover for podcast episodes.
  &__fit_img-box--circle {
    .books__fit_img {
      width: 120px;
      height: 120px;
      border: 1px solid $c-border-7;
      border-radius: 50%;
    }
  }
  &__img-box {
    pointer-events: none;
  }
}
.night-mode .books__fit_img-box {
  background-color: $c-dark-2;
}
.night-mode .books__fit_img-box--circle .books__fit_img {
  border-color: $c-dark-3;
}
// Constrain the list rows on mobile to have the same width for all doc covers.
.docs-listing__list {
  @media (max-width: $w-tablet-s) {
    .books__fit_img-box {
      flex-shrink: 0;
      width: 122px;
      height: auto;
      border-radius: 9px 0 0 9px;
    }
    .books__fit_img {
      width: 100%;
      height: 100%;
    }
    .books__fit_img-box--circle .books__fit_img {
      width: 88px;
      height: 88px;
    }
    .books__upper-box {
      padding-right: 32px;
    }
  }
}
// Listing blocks keep the vertical card on mobile, with less padding and no
// mobile progress bar.
.listing-block .books__more-menu {
  @media (max-width: $w-tablet-s) {
    bottom: 11px;
  }
}
</style>
